import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Input from "../../Atoms/Input/Input";
import "./Autocomplete.scss";
import { normalizeText } from "../../../utils/textUtils";
import Tooltip from "../../../components/Tooltip/Tooltip";
import { createPortal } from "react-dom";

const Autocomplete = ({
    options = [],
    value = null,
    onChange,
    placeholder,
    showOptionsOnFocus = true,
    maxVisibleOptions = 5,
    disabled = false,
    clearable = true,
    getOptionLabel = (option) => option?.label ?? "",
    getSearchText = (option) => getOptionLabel(option),

    isOptionEqualToValue = (option, value) =>
        option === value,

    noOptionsText = "Sin resultados",
    renderOption,
    dropdownClassName,

}) => {

    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const autocompleteRef = useRef(null);
    const OPTION_HEIGHT = 45;

    //PORTAL PARA DROPDOWN
    const [portalRoot, setPortalRoot] = useState(null);
    const [position, setPosition] = useState(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    //-----------------------------------------------------------
    // Sincroniza el texto mostrado en el input con el valor
    // seleccionado desde el componente padre.
    useEffect(() => {
        if (value) {
            setSearch(getOptionLabel(value));
        } else {
            setSearch("");
        }
    }, [value, getOptionLabel]);

    //-----------------------------------------------------------
    // Cierra el dropdown cuando el usuario hace click
    // fuera del componente.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                autocompleteRef.current &&
                !autocompleteRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener(
            "mousedown",
            handleClickOutside
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };
    }, []);
    //-----------------------------------------------------------
    //Crear portalRoot una vez
    useEffect(() => {
        let el = document.getElementById("dropdown-root");
        if (!el) {
            el = document.createElement("div");
            el.id = "dropdown-root";
            document.body.appendChild(el);
        }

        setPortalRoot(el);
    }, []);

    //-----------------------------------------------------------
    //Reposicionar cuando isOpen cambia / scroll / resize
    useEffect(() => {
        if (!isOpen) return;

        let frameId;

        const reposition = () => {
            cancelAnimationFrame(frameId);

            frameId = requestAnimationFrame(() => {
                updatePosition();
            });
        };

        reposition();

        window.addEventListener("resize", reposition);
        window.addEventListener("scroll", reposition, true);

        window.visualViewport?.addEventListener("resize", reposition);
        window.visualViewport?.addEventListener("scroll", reposition);

        return () => {
            cancelAnimationFrame(frameId);

            window.removeEventListener("resize", reposition);
            window.removeEventListener("scroll", reposition, true);

            window.visualViewport?.removeEventListener("resize", reposition);
            window.visualViewport?.removeEventListener("scroll", reposition);
        };
    }, [isOpen]);

    //-----------------------------------------------------------
    //calcular la posicion del dropdown
    const updatePosition = () => {
        if (!dropdownRef.current) return;

        const rect = dropdownRef.current.getBoundingClientRect();

        setPosition({
            position: "fixed",
            top: rect.bottom + 2,
            left: rect.left,
            width: rect.width,
            zIndex: 99999
        });
    };

    //------------------------------------------------------------------
    // Filtra las opciones ignorando acentos, mayúsculas
    // y espacios extra para mejorar la experiencia
    // de búsqueda.
    const filteredOptions = options.filter((option) =>
        normalizeText(getSearchText(option))
            .includes(normalizeText(search))
    );

    //___________________________________________________________________
    //HANDLERS 🚗
    //___________________________________________________________________
    // Notifica al componente padre el objeto seleccionado
    // y actualiza el texto mostrado en el input.
    const handleSelect = (option) => {
        onChange?.(option);
        setSearch(getOptionLabel(option));
        setIsOpen(false);
    };

    //limpiar input
    const handleClear = () => {
        onChange?.(null);
        setSearch("");
        setIsOpen(false);
    };

    //Navegacion con teclado (No terminado haria falta modificar input por tiempo se queda para despues)
    //
    // const handleKeyDown = (e) => {
    //     switch (e.key) {
    //         case "ArrowDown":
    //             e.preventDefault();

    //             setHighlightedIndex((prev) =>
    //                 Math.min(
    //                     prev + 1,
    //                     filteredOptions.length - 1
    //                 )
    //             );
    //             break;

    //         case "ArrowUp":
    //             e.preventDefault();

    //             setHighlightedIndex((prev) =>
    //                 Math.max(prev - 1, 0)
    //             );
    //             break;

    //         case "Enter":
    //             if (
    //                 highlightedIndex >= 0 &&
    //                 filteredOptions[highlightedIndex]
    //             ) {
    //                 handleSelect(
    //                     filteredOptions[highlightedIndex]
    //                 );
    //             }
    //             break;

    //         case "Escape":
    //             setIsOpen(false);
    //             break;

    //         default:
    //             break;
    //     }
    // };
    //-------------------------------------------------------------------
    // Determina si una opción corresponde al valor actual.
    // La comparación es configurable para soportar objetos
    // provenientes de distintas fuentes de datos.
    const isSelected = (option) => {
        if (!value) return false;

        return isOptionEqualToValue(option, value);
    };

    //-------------------------------------------------------------------
    // Resalta la coincidencia encontrada manteniendo
    // el texto original para preservar acentos y formato.
    const highlightMatch = (text = "", search = "") => {
        if (!search.trim()) return text;

        const normalizedText = normalizeText(text);
        const normalizedSearch = normalizeText(search);

        const startIndex = normalizedText.indexOf(normalizedSearch);

        if (startIndex === -1) return text;

        const endIndex = startIndex + normalizedSearch.length;

        return (
            <>
                {text.slice(0, startIndex)}
                <span className="autocomplete__highlight">
                    {text.slice(startIndex, endIndex)}
                </span>
                {text.slice(endIndex)}
            </>
        );
    };
    //-------------------------------------------------------------------
    //-------------------------------------------------------------------
    //-------------------------------------------------------------------

    return (
        <div
            className="autocomplete"
            ref={dropdownRef}
        >
            <Input
                placeholder={placeholder}
                value={search}
                disabled={disabled}
                onFocus={() => {
                    if (disabled) return;

                    if (showOptionsOnFocus) {
                        setPosition(null);
                        setIsOpen(true);
                    }
                }}
                onBlur={(e) => {
                    setTimeout(() => {
                        if (e.target.value === "") {
                            handleClear();
                        }
                    }, 150);
                }}
                onChange={(e) => {
                    if (disabled) return;
                    setSearch(e.target.value);
                    setIsOpen(true);
                }}
                ref={inputRef}
            />
            {clearable && value && (
                <Tooltip text={"Borrar selección"} positionX={'center'} positionY={'top'}>
                    <button
                        type="button"
                        className="autocomplete__clear"
                        onClick={handleClear}
                    >
                        ×
                    </button>
                </Tooltip>
            )}
            {isOpen && portalRoot && position &&
                createPortal(

                    <div
                        className={classNames("autocomplete__dropdown", dropdownClassName)}
                        style={{
                            ...position,
                            maxHeight: `${maxVisibleOptions * OPTION_HEIGHT}px`
                        }}
                    >

                        {filteredOptions.length === 0 ? (
                            <div className="autocomplete__empty">
                                {noOptionsText}
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.id ?? option.employee_id ?? index}
                                    className={classNames("autocomplete__option", {
                                        "autocomplete__option--selected": isSelected(option),
                                    })}
                                    onClick={() => handleSelect(option)}
                                >
                                    {renderOption ? renderOption(option) : highlightMatch(
                                        getOptionLabel(option),
                                        search
                                    )}
                                </div>
                            ))
                        )}
                    </div>,
                    portalRoot
                )
            }
        </div>
    );
};

export default Autocomplete;

/**
 * Componente de autocompletado reutilizable.
 *
 * Características:
 * - Búsqueda parcial sobre cualquier parte del texto.
 * - Ignora mayúsculas, minúsculas, acentos y espacios extra.
 * - Permite definir el texto mostrado y el texto utilizado para búsqueda.
 * - Retorna el objeto completo seleccionado mediante `onChange`.
 * - Soporta valor controlado mediante `value`.
 * - Permite limpiar la selección cuando `clearable` está activo.
 * - Muestra opciones al enfocar el input si `showOptionsOnFocus` está activo.
 * - Resalta coincidencias dentro de los resultados.
 * - Muestra mensaje personalizado cuando no hay resultados.
 * - Limita la altura del listado con base en `maxVisibleOptions`.
 * - Renderiza el dropdown mediante `createPortal` para evitar problemas de overflow en modales o contenedores.
 * - Recalcula la posición del dropdown al abrir, hacer scroll, resize o cambiar el viewport/zoom.
 * - Cierra automáticamente al hacer click fuera del autocomplete y del dropdown.
 *
 * Nota:
 * - La navegación por teclado está prevista, pero todavía no está activa completamente.
 *
 * @param {Array} options Lista de elementos disponibles.
 * @param {*} value Elemento seleccionado.
 * @param {Function} onChange Retorna el objeto seleccionado o `null`.
 * @param {String} placeholder Texto del input.
 * @param {Boolean} showOptionsOnFocus Muestra las opciones al recibir foco.
 * @param {Number} maxVisibleOptions Cantidad máxima de opciones visibles antes de activar scroll.
 * @param {Boolean} disabled Deshabilita la interacción.
 * @param {Boolean} clearable Permite borrar la selección actual.
 * @param {Function} getOptionLabel Retorna el texto visible de cada opción.
 * @param {Function} getSearchText Retorna el texto utilizado para filtrar.
 * @param {Function} isOptionEqualToValue Compara una opción contra el valor seleccionado.
 * @param {String} noOptionsText Mensaje cuando no hay resultados.
 * @param {Function} renderOption Contenido personalizado de la opción; conserva selección y contenedor.
 * @param {String} dropdownClassName Clase adicional exclusiva del listado renderizado en el portal.
 *
 * @example
 * <Autocomplete
 *   options={teachers}
 *   value={selectedTeacher}
 *   onChange={setSelectedTeacher}
 *   placeholder="Selecciona un maestro"
 *   getOptionLabel={(teacher) =>
 *     `${teacher.first_name} ${teacher.last_name}`
 *   }
 *   getSearchText={(teacher) =>
 *     `${teacher.first_name} ${teacher.last_name}`
 *   }
 *   isOptionEqualToValue={(option, value) =>
 *     option.employee_id === value.employee_id
 *   }
 * />
 */
